import { useHireflixPlanAutoRefill } from '@features/applications/hooks/useHireflixPlanAutoRefill';
import { useHireflixPurchasePlan } from '@features/applications/hooks/useHireflixPurchasePlan';
import CloseIcon from '@mui/icons-material/Close';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
  Container,
  Grid,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { hireflixCta } from '@styles/colors';
import { useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { features, plans } from './constants';

type HireflixPlanPurchaseModalProps = {
  isOpen: boolean;
  handleClose: () => void;
};

const HireflixPlanPurchaseModal = ({
  isOpen,
  handleClose,
}: HireflixPlanPurchaseModalProps) => {
  const { authData } = useContext(authContext);
  const autoRefillDefaultValue =
    authData?.user?.company?.hire_flix_plan?.auto_refill;
  const [errorText, setErrorText] = useState('');
  const [selected, setSelected] = useState<number>(2);

  const [autoRefill, setAutoRefill] = useState<boolean>(() =>
    Boolean(autoRefillDefaultValue),
  );

  useEffect(() => {
    if (isOpen) {
      setAutoRefill(Boolean(autoRefillDefaultValue));
    }
  }, [autoRefillDefaultValue, isOpen]);

  const { hireflixPurchasePlan, isHireflixPurchasePlanLoading } =
    useHireflixPurchasePlan({ setErrorText });

  const { hireflixPlanAutoRefill, isHireflixPlanAutoRefillLoading } =
    useHireflixPlanAutoRefill({ setErrorText });

  // const token = 'tok_1SMs0PCpbUAwddyegp7DUnEI';

  const handlePurchase = async () => {
    const plan = plans[selected];

    try {
      setErrorText('');
      hireflixPurchasePlan(
        {
          credit_limit: plan.credits,
          amount: plan.price,
          type: 'existing',
          // token,
          // auto_refill: autoRefill,
        },
        {
          onSuccess: (data) => {
            toast.success('HireFlix Credit Purchase successfully');
            if (data?.status === 200) {
              handleClose();
              console.log('data =>', data);
            }
          },
        },
      );
    } catch (err) {
      console.error('Purchase failed:', err);
      setErrorText('Purchase failed. Please try again.');
    }
  };

  const handlePlanAutoRefill = async (value?: boolean) => {
    const payloadValue = typeof value === 'boolean' ? value : autoRefill;
    try {
      setErrorText('');
      hireflixPlanAutoRefill(
        {
          auto_refill: payloadValue,
        },
        {
          onSuccess: (data) => {
            toast.success(
              data?.message || 'HireFlix Auto Refills successfully',
            );
            if (data?.status === 200) {
              console.log('data =>', data);
            }
          },
        },
      );
    } catch (err) {
      console.error('Auto refill failed:', err);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '896px !important',
          backgroundColor: 'rgb(247, 247, 247)',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', position: 'relative' }}>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '64px',
            height: '64px',
            margin: '0 auto',
            borderRadius: '100%',
            backgroundColor: 'rgb(243, 232, 255)',
            mb: 1,
          }}
        >
          <VideocamRoundedIcon
            sx={{ fontSize: 40, color: 'rgb(126, 34, 206)' }}
          />
        </Box>
        <Typography variant="h4" fontWeight={600} sx={{ p: 3, mb: 1 }}>
          HireFlix Interview Credits
          <Typography sx={{ mt: 0.5 }}>
            <b>50</b> HireFlix Interview Credits Available
          </Typography>
        </Typography>

        {errorText && (
          <Box
            sx={{
              maxWidth: '768px !important',
              width: '100% !important',
              margin: '0 auto',
            }}
          >
            <Alert
              severity="error"
              sx={{
                borderRadius: '8px',
                backgroundColor: 'rgb(251, 224, 224) !important',
                color: 'rgb(214, 71, 71) !important',
              }}
            >
              {errorText}
            </Alert>
          </Box>
        )}
      </DialogTitle>

      <DialogContent>
        <Container
          disableGutters
          sx={{ maxWidth: '768px !important', width: '100% !important' }}
        >
          <Grid container spacing={3}>
            {features.map((item) => {
              const Icon = item.icon;
              return (
                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    key={item.text}
                    sx={{
                      bgcolor: 'rgb(250, 245, 255)',
                      borderRadius: 8,
                      px: 3,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      color: 'rgb(126, 34, 206)',
                      textAlign: 'left',
                      border: '1px solid rgb(233, 213, 255)',
                    }}
                  >
                    <Icon
                      sx={{ color: 'rgb(126, 34, 206)', fontSize: '22px' }}
                    />
                    <Typography fontSize="14px">{item.text}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>

        <Box
          sx={{
            position: 'relative',
            maxWidth: '782px',
            width: '100%',
            margin: '0 auto',
            borderRadius: 10,
          }}
        >
          <Box
            sx={{
              filter:
                isHireflixPlanAutoRefillLoading || isHireflixPurchasePlanLoading
                  ? 'blur(3px)'
                  : 'none',
              pointerEvents:
                isHireflixPlanAutoRefillLoading || isHireflixPurchasePlanLoading
                  ? 'none'
                  : 'auto',
            }}
          >
            <Divider
              sx={{
                borderBottomColor: 'rgb(229, 231, 235)',
                maxWidth: '782px',
                width: '100%',
                margin: '40px auto 40px',
              }}
            />

            <Container
              disableGutters
              sx={{
                maxWidth: '768px !important',
                width: '100% !important',
              }}
            >
              <Grid container spacing={2} columns={10}>
                {plans.map((plan, idx) => (
                  <Grid item xs={5} sm={3.33} md={2}>
                    <Box
                      key={plan.credits}
                      onClick={() => setSelected(idx)}
                      sx={{
                        cursor: 'pointer',
                        textAlign: 'center',
                        px: 2,
                        py: selected === idx ? 5.5 : 3,
                        border: '1px solid rgb(230, 230, 230)',
                        borderRadius: 12,
                        boxShadow:
                          selected === idx
                            ? 'rgb(255, 255, 255) 0px 0px 0px 0px, rgb(126, 34, 206) 0px 0px 0px 2px, rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.1) 0px 8px 10px -6px'
                            : '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out',
                        position: 'relative',
                        backgroundColor: '#fff',
                      }}
                    >
                      {plan.popular && (
                        <Typography
                          sx={{
                            position: 'absolute',
                            top: -16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: 'rgb(243, 232, 255)',
                            color: 'rgb(126, 34, 206)',
                            padding: '7px 5px',
                            borderRadius: '8px',
                            fontSize: '15px',
                            lineHeight: '15px',
                            fontWeight: 600,
                            width: 1,
                            maxWidth: 'calc(100% - 25px)',
                          }}
                        >
                          Most Popular
                        </Typography>
                      )}

                      <Typography fontWeight={600} mb={2}>
                        {plan.credits} Credits
                      </Typography>
                      <Typography fontSize="0.85rem" color="text.secondary">
                        ${plan.rate.toFixed(2)}/interview
                      </Typography>
                      {plan.save > 0 ? (
                        <Typography
                          fontSize="0.8rem"
                          color="success.main"
                          sx={{
                            backgroundColor: 'rgb(220, 252, 231)',
                            maxWidth: 'fit-content',
                            fontWeight: '600',
                            borderRadius: 4,
                            padding: '1px 10px',
                            margin: '5px auto 0',
                          }}
                        >
                          Save {plan.save}%
                        </Typography>
                      ) : (
                        <div style={{ height: '21.2px', marginTop: '5px' }} />
                      )}
                      <Typography
                        fontWeight={700}
                        sx={{
                          mt: 2.5,
                          color: 'rgb(126, 34, 206)',
                          fontSize: '20px',
                        }}
                      >
                        ${plan.price.toFixed(2)}
                      </Typography>

                      {selected === idx && (
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            mt: 3.5,
                            textTransform: 'none',
                            fontSize: '14px',
                            pl: '12px',
                            pr: '12px',
                          }}
                          disabled={
                            isHireflixPurchasePlanLoading ||
                            isHireflixPlanAutoRefillLoading
                          }
                          onClick={handlePurchase}
                          startIcon={
                            isHireflixPurchasePlanLoading && (
                              <CircularProgress size="17px" />
                            )
                          }
                        >
                          Purchase
                        </Button>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>

            <Box
              sx={{
                maxWidth: '600px',
                width: 1,
                margin: '0 auto',
              }}
            >
              <Typography
                variant="body2"
                textAlign="center"
                sx={{ mt: 6, color: 'text.secondary', mb: 5 }}
              >
                You’re never charged for invites – Credits Never Expire.
                <br />
                Credits are only deducted when candidates complete their
                HireFlix video interviews.
              </Typography>

              <Box
                sx={{
                  mt: 3,
                  mb: 5,
                  borderRadius: 10,
                  px: 3,
                  py: 3,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#fff',
                  border: '1px solid rgb(230, 230, 230)',
                  boxShadow:
                    'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
                }}
              >
                <Box>
                  <Typography fontWeight={500}>
                    Auto Refill Current Package Selection
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Never miss an interview – Automatically refill credits when
                    balance reaches zero
                  </Typography>
                </Box>

                <FormControlLabel
                  disabled={
                    isHireflixPlanAutoRefillLoading ||
                    isHireflixPurchasePlanLoading
                  }
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={autoRefill}
                      onChange={({ target: { checked } }) => {
                        setAutoRefill(checked);
                        handlePlanAutoRefill(checked);
                      }}
                    />
                  }
                  label=""
                />
              </Box>
            </Box>
          </Box>

          {(isHireflixPlanAutoRefillLoading ||
            isHireflixPurchasePlanLoading) && (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2,
              }}
            >
              <CircularProgress sx={{ color: hireflixCta }} />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HireflixPlanPurchaseModal;
